import {CommonModule} from '@angular/common';
import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FeatureFlagService, ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  DerivedUnderwritingProfileStatus,
  DocumentName,
  ErrorResponseCode,
  ExtraRequirementsInquiry,
  FeatureFlags,
  ModificationNoticeBreakdown,
  QuestionnaireId,
  TextTranslations,
} from '@emma-labs/shared/typescript';
import {BadgeModule} from 'primeng/badge';
import {CardModule} from 'primeng/card';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ToastModule} from 'primeng/toast';
import {Observable} from 'rxjs';
import {first} from 'rxjs/operators';
import {UnderwritingProfilePageObject} from '../../entities/underwriting-page-object/underwriting-page-object';
import {UnderwritingProfilePageOrchestrator} from '../../services/underwriting/underwriting-profile-page.orchestrator';
import {ExtraQuestionnairesSectionComponent} from '../archived-proposal-page/extra-questionnaires-section/extra-questionnaires-section.component';
import {MainComponent} from '../main/main.component';
import {SkeletonComponent} from '../main/skeleton/skeleton.component';
import {HealthQuestionnaireSectionComponent} from '../proposal-page/health-questionnaire-section/health-questionnaire-section.component';
import {ClientInfoSectionComponent} from './client-info-section/client-info-section.component';
import {ExtraRequirementsSectionComponent} from './extra-requirements-section/extra-requirements-section.component';
import {ModificationSectionComponent} from './modification-section/modification-section.component';
import {NotesSectionComponent} from './notes-section/notes-section.component';
import {ProfileInfoComponent} from './profile-info/profile-info.component';
import {ProposalNoteSectionComponent} from './proposal-note-section/proposal-note-section.component';
import {RequestableExtraQuestionnairesSectionComponent} from './requestable-extra-questionnaires-section/requestable-extra-questionnaires-section.component';
import {UnderwritingInfoCardComponent} from './underwriting-info-card/underwriting-info-card.component';

@Component({
  selector: 'em-underwriting-page',
  templateUrl: './underwriting-profile-page.component.html',
  imports: [
    CommonModule,
    MainComponent,
    ProfileInfoComponent,
    ClientInfoSectionComponent,
    HealthQuestionnaireSectionComponent,
    ModificationSectionComponent,
    NotesSectionComponent,
    ToastModule,
    RequestableExtraQuestionnairesSectionComponent,
    CardModule,
    BadgeModule,
    ExtraQuestionnairesSectionComponent,
    ConfirmDialogModule,
    SkeletonComponent,
    ExtraRequirementsSectionComponent,
    UnderwritingInfoCardComponent,
    ProposalNoteSectionComponent,
  ],
})
export class UnderwritingProfilePageComponent implements OnDestroy {
  pageObject$: Observable<UnderwritingProfilePageObject>;
  isCancelButtonLoading = false;
  isManuallyAddedToContractEnabled = false;
  isReplacementNoticeDownloadEnabled = false;
  readonly status = DerivedUnderwritingProfileStatus;
  protected readonly DocumentName = DocumentName;

  constructor(
    readonly route: ActivatedRoute,
    private readonly orchestrator: UnderwritingProfilePageOrchestrator,
    private readonly translate: TranslationService,
    private readonly toastService: ToastService,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    if (!route.snapshot.params['underwritingProfileId']) {
      throw new Error('No underwriting profile id found');
    }

    this.pageObject$ = orchestrator.get(atob(route.snapshot.params['underwritingProfileId']));

    this.featureFlagService
      .isEnabled(FeatureFlags.IS_MANUALLY_ADDED_TO_CONTRACT_ENABLED)
      .pipe(first())
      .subscribe((isEnabled) => {
        this.isManuallyAddedToContractEnabled = isEnabled;
      });

    this.featureFlagService
      .isEnabled(FeatureFlags.IS_UP_POLICY_REPLACEMENT_DOWNLOAD_ENABLED)
      .pipe(first())
      .subscribe((isEnabled) => (this.isReplacementNoticeDownloadEnabled = isEnabled));
  }

  saveNote(event: {output: string; callback?: (status: boolean) => void}) {
    this.orchestrator
      .saveNote(event.output)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: (error) => {
          this.handleError(error.code);
          event.callback?.(false);
        },
      });
  }

  saveUnderwriter(event: {output: string | undefined}) {
    this.orchestrator
      .saveUnderwriter(event.output)
      .pipe(first())
      .subscribe({
        error: (error) => this.handleError(error.code),
      });
  }

  updateIsIncludedInContract(event: {isIncludedInContract: boolean; healthAnswersRevisionId: string}) {
    this.orchestrator
      .updateIsIncludedInContract(event.isIncludedInContract, event.healthAnswersRevisionId)
      .pipe(first())
      .subscribe({
        error: (error) => this.handleError(error.code),
      });
  }

  updateSurchargeRate(surchargeRate: number) {
    this.orchestrator
      .updateSurchargeRate(surchargeRate)
      .pipe(first())
      .subscribe({
        error: (error) => this.handleError(error.code),
      });
  }

  onProfileRefused(event: {refusalMessage: TextTranslations; callback: (status: boolean) => void}): void {
    this.orchestrator
      .refuseProfile(event.refusalMessage)
      .pipe(first())
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'Le client a été refusé avec succès.'
              : 'The client was successfully refused.',
          );
          event.callback(true);
        },
        error: (error) => {
          this.handleError(error.code);
          event.callback(false);
        },
      });
  }

  onProfileApproved(event: {
    callback?: (status: boolean) => void;
    modificationNoticeBreakdown?: ModificationNoticeBreakdown;
  }): void {
    this.orchestrator
      .approveProfile(event.modificationNoticeBreakdown)
      .pipe(first())
      .subscribe({
        next: () => {
          event.callback?.(true);
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'Le client a été approuvé avec succès.'
              : 'The client was successfully approved.',
          );
        },
        error: (error) => {
          event.callback?.(false);
          this.handleError(error.code);
        },
      });
  }

  createHealthAnswersRevision(event: {
    output: QuestionnaireId[] | string;
    callback?: (status: boolean) => void;
  }) {
    this.orchestrator
      .createHealthAnswersRevision(event.output)
      .pipe(first())
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'Le ou les questionnaires supplémentaires ont été ajoutés avec succès.'
              : 'One or more additional questionnaires have been successfully added.',
          );
          event.callback?.(true);
        },
        error: (error) => {
          this.handleError(error.code);
          event.callback?.(false);
        },
      });
  }

  submitExtraRequirements(event: {
    output: Omit<ExtraRequirementsInquiry, 'id' | 'createdAt'>;
    callback?: (status: boolean) => void;
  }) {
    this.orchestrator
      .submitExtraRequirements(event.output)
      .pipe(first())
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'Les exigences de sélection ont bien été commandées.'
              : 'The additional requirements have been successfully ordered.',
          );
          event.callback?.(true);
        },
        error: (error) => {
          this.handleError(error.code);
          event.callback?.(false);
        },
      });
  }

  downloadDocument($event: {
    documentName: string | DocumentName;
    type?: 'haRevision' | 'extraRequirements';
    id?: string;
    callback?: () => void;
  }) {
    let downloadObservable$;
    if (this.isReplacementNoticeDownloadEnabled) {
      if ($event.documentName === DocumentName.REPLACEMENT_NOTICE) {
        downloadObservable$ = this.orchestrator.getReplacementNoticeDocument();
      } else {
        downloadObservable$ =
          $event.type === 'haRevision'
            ? this.orchestrator.getHealthAnswersRevisionDocument($event.id!, $event.documentName)
            : this.orchestrator.getExtraRequirementsDocument($event.documentName);
      }
    } else {
      downloadObservable$ =
        $event.type === 'haRevision'
          ? this.orchestrator.getHealthAnswersRevisionDocument($event.id!, $event.documentName)
          : this.orchestrator.getExtraRequirementsDocument($event.documentName);
    }

    downloadObservable$.pipe(first()).subscribe({
      next: (blob) => {
        const file = new Blob([blob], {type: 'application/pdf'});
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, '_blank');
        $event.callback?.();
      },
      error: (error) => {
        this.handleError(error.code);
        $event.callback?.();
      },
    });
  }

  uploadDocuments(event: {documents: File[]; callback: (status: boolean) => void}) {
    this.orchestrator
      .uploadExtraRequirementsDocuments(event.documents)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: (error) => {
          this.handleError(error.code);
          event.callback?.(false);
        },
      });
  }

  removeDocument(event: {documentName: string; callback: (status: boolean) => void}) {
    this.orchestrator
      .removeExtraRequirementsDocument(event.documentName)
      .pipe(first())
      .subscribe({
        next: () => event.callback?.(true),
        error: (error) => {
          this.handleError(error.code);
          event.callback?.(false);
        },
      });
  }

  cancel() {
    this.isCancelButtonLoading = true;
    this.orchestrator
      .cancelProfile()
      .pipe(first())
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translate.currentLanguageIsFr()
              ? 'La proposition a maintenant le statut "Sans suivi".'
              : 'The proposal now has the "No follow-up" status.',
          );
          this.isCancelButtonLoading = false;
        },
        error: (error) => {
          this.handleError(error.code);
          this.isCancelButtonLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.orchestrator.clear();
  }

  private handleError(errorCode?: ErrorResponseCode): void {
    const isFr = this.translate.currentLanguageIsFr();

    switch (errorCode) {
      case ErrorResponseCode.FORBIDDEN: {
        this.toastService.error(
          isFr
            ? `Vous n’avez pas les droits nécessaires pour effectuer cette action.`
            : 'You do not have the necessary permissions to perform this action.',
        );
        break;
      }
      default: {
        this.toastService.error(
          isFr ? `Une erreur s'est produite. Veuillez réessayer.` : 'An error occurred. Please try again.',
        );
      }
    }
  }
}
